/**
 * Module registry. Which modules a bundle contains is decided by its entry
 * point, which calls `registerModules` before anything else runs — a module
 * left out of that call is not compiled in at all. After the config loads,
 * `installEnabledModules` activates the registered ones the config (and
 * resolved options) enable.
 *
 * Adding a module = creating a `module.ts` with the standard shape and adding
 * it to `ALL_MODULES`.
 */

import { discardKzqAdBuffer } from '../core/kzq';
import { debug, warn } from '../util/log';
import type { SdkModuleDefinition, SdkModuleId, SdkContext } from './types';
// Type-only, so the registry itself pulls no module code into the bundle.
import type { PoolModuleApi } from './pool/api';
import type { EcomModuleApi } from './ecom/api';
import type { PixelsModuleApi } from './pixels/module';
import type { MbpModuleApi } from './mbp/api';
import type { PrivacyModuleApi } from './privacy/module';

export interface ModuleApiMap {
  privacy: PrivacyModuleApi;
  pool: PoolModuleApi;
  ecom: EcomModuleApi;
  pixels: PixelsModuleApi;
  mbp: MbpModuleApi;
}

let MODULES: readonly SdkModuleDefinition[] = [];

/** Called by the bundle entry point before anything else. Order is install order. */
export function registerModules(modules: readonly SdkModuleDefinition[]): void {
  MODULES = modules;
}

const installed = new Map<SdkModuleId, unknown>();

/**
 * `only` installs a subset — init uses it to bring up privacy on its own, so
 * the consent gate can answer before the rest of the SDK exists. The kzq
 * discard is skipped for a partial pass: mbp may still be coming.
 */
export function installEnabledModules(ctx: SdkContext, only?: readonly SdkModuleId[]): void {
  const config = ctx.getConfig();
  const options = ctx.getOptions();
  for (const module of MODULES) {
    if (only && !only.includes(module.id)) continue;
    if (installed.has(module.id)) continue;
    let enabled = false;
    try {
      enabled = module.isEnabled(config, options);
    } catch (err) {
      warn(`[kueez-sdk] module ${module.id} isEnabled threw`, err);
    }
    if (!enabled) {
      debug(`module skipped: ${module.id}`);
      continue;
    }
    try {
      installed.set(module.id, module.install(ctx) ?? {});
      debug(`module installed: ${module.id}`);
    } catch (err) {
      warn(`[kueez-sdk] module ${module.id} install threw`, err);
    }
  }
  // Publisher kzq stub may still be listening; drop it when mbp will not take over.
  if (!only && !installed.has('mbp')) {
    discardKzqAdBuffer();
  }
  debug('modules ready', { installed: [...installed.keys()] });
}

export function getInstalledModule<K extends SdkModuleId>(id: K): ModuleApiMap[K] | null {
  return (installed.get(id) as ModuleApiMap[K] | undefined) ?? null;
}

/** Like `getInstalledModule`, but throws a descriptive error for host misuse. */
export function requireModule<K extends SdkModuleId>(id: K): ModuleApiMap[K] {
  const api = getInstalledModule(id);
  if (api) return api;
  throw new Error(
    `[kueez-sdk] module "${id}" is not installed. It activates after init() when the website config enables it — access it inside ready()/onReady, and check the config (e.g. ecom_active) if it is still missing.`,
  );
}

export function isModuleInstalled(id: SdkModuleId): boolean {
  return installed.has(id);
}

/** Test-only */
export function _resetModulesForTests(): void {
  installed.clear();
}
